import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { apiError, isNetworkError } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { PageHead, EmptyState } from "@/components/storefront/common";
import { formatINR } from "@/lib/format";
import { resolveMediaUrl, handleMediaError } from "@/lib/media";
import { toast } from "sonner";
import { ShoppingBag, Tag, Loader2, CheckCircle2, MapPin, XCircle } from "lucide-react";
import { clearCheckoutRequestId, getCheckoutRequestId } from "./checkoutRequest";
import { isCodAvailable } from "./codAvailability";
import { captureAnalyticsEvent } from "@/lib/analytics";

const loadRazorpay = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

export default function Checkout() {
  const { cart, cartSubtotal, clearCart, settings } = useStore();
  const { user } = useAuth();
  const navigate = useNavigate();
  const store = settings?.store || {};
  const [addr, setAddr] = useState({ fullName: user?.name || "", phone: user?.phone || "", line1: "", line2: "", city: "", state: "", pincode: "", landmark: "" });
  const [email, setEmail] = useState(user?.email || "");
  const [delivery, setDelivery] = useState("COURIER");
  const [payment, setPayment] = useState("RAZORPAY");
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null);
  const [couponError, setCouponError] = useState(null);
  const [applying, setApplying] = useState(false);
  // Guards against a repeated click firing a second validation in the same tick
  // (the `applying` state is not visible to the second click yet) and against a
  // slow quote response overwriting a newer one.
  const applyingRef = useRef(false);
  const quoteReqRef = useRef(0);
  const checkoutStartedRef = useRef(false);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [terms, setTerms] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [notes, setNotes] = useState("");
  const [serviceability, setServiceability] = useState(null);
  const [checkingPin, setCheckingPin] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [addrConfirmed, setAddrConfirmed] = useState(false);
  const codAvailable = isCodAvailable(store, delivery, addr.pincode, serviceability);

  useEffect(() => {
    if (cart.length === 0 || checkoutStartedRef.current) return;
    checkoutStartedRef.current = true;
    captureAnalyticsEvent("checkout_started", {
      item_count: cart.reduce((count, item) => count + item.quantity, 0),
      currency: "INR",
    });
  }, [cart]);

  useEffect(() => {
    if (!codAvailable && payment === "COD") setPayment("RAZORPAY");
  }, [codAvailable, payment]);

  useEffect(() => {
    if (!user) { setSavedAddresses([]); return; }
    api.get("/addresses").then(({ data }) => setSavedAddresses(data.addresses || [])).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (delivery !== "COURIER") { setServiceability(null); return; }
    setServiceability(null);
    if (!/^\d{6}$/.test(addr.pincode)) { setCheckingPin(false); return; }
    let active = true;
    setCheckingPin(true);
    api.get(`/shipping/serviceability?pincode=${addr.pincode}`)
      .then(({ data }) => {
        if (!active) return;
        setServiceability(data);
        captureAnalyticsEvent("delivery_check_completed", {
          availability: data.serviceable === true
            ? "available"
            : data.serviceable === false ? "unavailable" : "unknown",
        });
      })
      .catch(() => {
        if (!active) return;
        setServiceability(null);
        captureAnalyticsEvent("delivery_check_completed", { availability: "unknown" });
      })
      .finally(() => { if (active) setCheckingPin(false); });
    return () => { active = false; };
  }, [addr.pincode, delivery]);

  // The backend is the single source of truth for what the customer is charged: it
  // re-evaluates the automatic rules, the applied coupon (only ONE discount — an
  // explicitly applied coupon always wins over an automatic rule and they are
  // never stacked) and the courier shipping charge for the entered destination —
  // which may be a live Delhivery rate, the free-shipping threshold, or the
  // store's flat rate. Shipping/tax/handling/total therefore come from the quote
  // instead of being recomputed in the browser.
  //
  // Every response is tagged so a slow reply from an earlier cart/coupon/address
  // state can never overwrite a newer one, and the same calculation runs again
  // when the order is created.
  useEffect(() => {
    if (cart.length === 0) { setQuote(null); return; }
    const reqId = ++quoteReqRef.current;
    setQuoteLoading(true);
    api.post("/cart/quote", {
      items: cart.map((i) => ({ productId: i.productId, variantId: i.variantId, quantity: i.quantity })),
      couponCode: applied?.code || null,
      deliveryMethod: delivery,
      paymentMethod: payment,
      // Only a complete pincode is sent: while the customer is still typing one,
      // the previous (valid) quote stays put instead of flickering.
      pincode: delivery === "COURIER" && /^\d{6}$/.test(addr.pincode) ? addr.pincode : null,
    }).then(({ data }) => {
      if (reqId !== quoteReqRef.current) return;
      setQuote(data);
      // A coupon that no longer qualifies (cart changed, expired, limit reached)
      // is dropped so the displayed total never promises a discount we can't give
      // — the reason is explained and the code goes back into the box so the
      // customer can see and adjust it. The eligible automatic discount is
      // recalculated by the quote below (no coupon) and applies again.
      if (data.couponError && applied) {
        setApplied(null);
        setCoupon((c) => c || applied.code || "");
        setCouponError(data.couponError);
      }
    }).catch((err) => {
      if (reqId === quoteReqRef.current) setQuote(null);
      console.error("Checkout pricing unavailable:", err?.message);
    }).finally(() => {
      if (reqId === quoteReqRef.current) setQuoteLoading(false);
    });
  }, [cart, applied, delivery, addr.pincode, payment]);

  if (cart.length === 0)
    return (
      <div className="container-x py-8">
        <PageHead title="Checkout" />
        <EmptyState icon={ShoppingBag} title="Your cart is empty" text="Add products before checking out.">
          <Link to="/shop" className="bg-slate-900 text-white font-semibold px-6 py-3 rounded-lg">Go to Shop</Link>
        </EmptyState>
      </div>
    );

  // Single applied discount: the coupon the customer applied when there is one,
  // else the highest automatic rule. `quote` is authoritative; the coupon's own
  // amount is only a placeholder until the quote for it arrives.
  const discount = quote ? quote.discount : (applied?.discount || 0);
  const discountLabel = quote?.discountLabel || applied?.code || null;
  const discountIsAuto = quote ? quote.discountSource === "auto" : false;
  // The subtotal behind the totals is the backend's own (it recomputes every price
  // from the database), falling back to the local cart only while the quote loads,
  // so the summary matches the amount that will actually be charged.
  const subtotal = quote ? quote.subtotal : cartSubtotal;
  // Shipping, tax, handling and the payable total are the backend's own numbers
  // (the same ones checkout charges). Until the first quote arrives they show as
  // "calculating" rather than a made-up amount, and a courier charge that could
  // not be determined is never shown as free.
  const shipping = quote ? quote.shippingCharge : null;
  const tax = quote ? quote.tax : 0;
  const handling = quote ? quote.handlingFee : 0;
  const codFee = quote ? (quote.codFee || 0) : 0;
  const total = quote ? quote.payableTotal : null;
  const shippingEnabled = quote?.shipping?.enabled ?? false;
  const shippingPriced = shipping !== null && shipping !== undefined;
  const shippingPending = !quote || quoteLoading || (delivery === "COURIER" && !shippingPriced);
  // `prepaidEligible === false` is the courier saying prepaid delivery is refused for
  // this pincode (non-serviceable, embargoed, or pre_paid not offered when enforced).
  const prepaidUnavailable = delivery === "COURIER" && serviceability?.prepaidEligible === false;
  const notServiceable = delivery === "COURIER"
    && ((serviceability && serviceability.serviceable === false) || prepaidUnavailable);

  // Warn (not block) when the entered city/state looks inconsistent with the pincode's known location.
  const norm = (s) => (s || "").trim().toLowerCase();
  const pinCity = serviceability?.city;
  const pinState = serviceability?.state;
  const cityMismatch = !!(serviceability?.serviceable && pinCity && addr.city &&
    !norm(addr.city).includes(norm(pinCity)) && !norm(pinCity).includes(norm(addr.city)));

  const applySavedAddress = (a) => {
    setAddr({
      fullName: a.fullName || "", phone: (a.phone || "").replace(/\D/g, "").slice(0, 10),
      line1: a.line1 || "", line2: a.line2 || "", city: a.city || "", state: a.state || "",
      pincode: (a.pincode || "").replace(/\D/g, "").slice(0, 6), landmark: a.landmark || "",
    });
    setAddrConfirmed(false);
    toast.success("Delivery details filled from your saved address");
  };
  const clearAddress = () => {
    setAddr({ fullName: user?.name || "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "", landmark: "" });
    setAddrConfirmed(false);
  };

  const applyCoupon = async () => {
    const code = coupon.trim().toUpperCase();
    if (!code) return;
    // Repeats (double click / Enter + click) must not fire a second request or a
    // second toast while one validation is in flight.
    if (applyingRef.current) return;
    if (applied?.code === code) return;              // already applied
    if (!user) {
      setCouponError("Please log in to apply coupons");
      toast.error("Please log in to apply coupons");
      return;
    }
    applyingRef.current = true;
    setApplying(true);
    setCouponError(null);
    try {
      // Validate against the backend's own subtotal for this cart. The local cart
      // total is only a fallback while the first quote is still loading — the
      // client's prices are never authoritative.
      const { data } = await api.post("/coupons/validate", { code, subtotal: quote?.subtotal ?? cartSubtotal });
      setApplied(data);
      setCoupon("");                                  // clear the box; the applied coupon stays visible as a removable label
      toast.success(`Coupon ${data.code} applied — you save ${formatINR(data.discount)}`);
    } catch (e) {
      setApplied(null);
      // The code stays in the box so it can be corrected. A rejected coupon is a
      // normal 400 carrying a human-readable reason; only a genuine transport
      // failure (no HTTP response at all) is reported as a connection problem, so
      // the two can never be confused again.
      const message = isNetworkError(e)
        ? "We couldn't reach the server. Please check your connection and try again."
        : apiError(e);
      setCouponError(message);
      toast.error(message);
    } finally {
      applyingRef.current = false;
      setApplying(false);
    }
  };

  // Removing the coupon re-runs the quote above without a coupon, so the eligible
  // automatic discount is recalculated and applied again.
  const removeCoupon = () => { setApplied(null); setCoupon(""); setCouponError(null); };

  const placeOrder = async () => {
    if (!terms) { toast.error("Please accept the terms to continue"); return; }
    for (const [k, label] of [["fullName", "Full name"], ["phone", "Phone"], ["line1", "Address"], ["city", "City"], ["state", "State"], ["pincode", "Pincode"]]) {
      if (!addr[k]?.trim()) { toast.error(`${label} is required`); return; }
    }
    if (delivery === "COURIER") {
      if (!/^[6-9]\d{9}$/.test(addr.phone)) { toast.error("Enter a valid 10-digit Indian mobile number"); return; }
      if (!/^[1-9]\d{5}$/.test(addr.pincode)) { toast.error("Enter a valid 6-digit pincode"); return; }
      if (addr.line1.trim().length < 8) { toast.error("Please enter a more complete address (house/street details)"); return; }
      if (notServiceable) { toast.error("We can't deliver to this pincode. Try store pickup or a different address."); return; }
      if (!shippingPriced) { toast.error(quote?.shippingMessage || "We couldn't calculate the delivery charge for this address. Please retry, or choose store pickup."); return; }
      if (!addrConfirmed) { toast.error("Please confirm your delivery address is complete and accurate"); return; }
    }
    setPlacing(true);
    try {
      const items = cart.map((i) => ({ productId: i.productId, variantId: i.variantId, quantity: i.quantity }));
      const addressPayload = { ...addr };
      const clientRequestId = getCheckoutRequestId(cart, {
        address: addressPayload,
        couponCode: applied?.code || null,
        deliveryMethod: delivery,
        paymentMethod: payment,
        notes,
        email: user ? null : email,
        phone: user ? null : addr.phone,
      });
      const { data } = await api.post("/checkout", {
        items, address: addressPayload, couponCode: applied?.code || null,
        deliveryMethod: delivery, paymentMethod: payment, notes,
        clientRequestId,
        email: user ? null : email, phone: user ? null : addr.phone,
      });
      const order = data.order;
      const gw = data.payment?.gateway;

      if (payment === "COD") {
        clearCart();
        clearCheckoutRequestId();
        toast.success("Order placed. Please pay the amount due when your order is delivered.");
        navigate(`/order-success/${order.id}`, { state: { order } });
        return;
      }

      if (!gw?.configured || !gw?.id) {
        captureAnalyticsEvent("checkout_error", { error_category: "payment_setup" });
        toast.error(gw?.message || "Online payment is temporarily unavailable. Your cart is unchanged; please retry.");
        setPlacing(false);
        return;
      }

      const ok = await loadRazorpay();
      if (!ok) {
        captureAnalyticsEvent("checkout_error", { error_category: "payment_setup" });
        toast.error("Could not load the payment window. Check your connection.");
        setPlacing(false);
        return;
      }

      const rzp = new window.Razorpay({
        key: gw.keyId,
        amount: gw.amount,
        currency: gw.currency || "INR",
        order_id: gw.id,
        name: "Shree Shyam Mobile",
        description: `Order ${order.orderNumber}`,
        prefill: { name: addr.fullName, email: user?.email || email, contact: addr.phone },
        theme: { color: "#f59e0b" },
        handler: async (resp) => {
          try {
            const { data: verification } = await api.post("/payments/verify", {
              orderId: order.id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            clearCart();
            clearCheckoutRequestId();
            toast.success("Payment successful! Order confirmed.");
            navigate(`/order-success/${order.id}`, { state: { order: verification.order || order } });
          } catch (e) {
            captureAnalyticsEvent("checkout_error", { error_category: "payment_verification" });
            toast.error("Payment verification failed. If money was deducted it will be auto-refunded.");
            setPlacing(false);
          }
        },
        modal: { ondismiss: () => { setPlacing(false); toast.message("Payment cancelled. Your order is saved as pending."); } },
      });
      rzp.on("payment.failed", () => {
        captureAnalyticsEvent("checkout_error", { error_category: "payment_failed" });
        setPlacing(false);
        toast.error("Payment failed. Please try again.");
      });
      captureAnalyticsEvent("payment_attempted", {
        payment_method: "RAZORPAY",
        item_count: items.reduce((count, item) => count + item.quantity, 0),
        currency: "INR",
      });
      rzp.open();
    } catch (e) {
      captureAnalyticsEvent("checkout_error", { error_category: "order_submission" });
      toast.error(apiError(e));
      setPlacing(false);
    }
  };

  const field = (label, key, opts = {}) => (
    <div className={opts.full ? "sm:col-span-2" : ""}>
      <label className="text-xs font-medium text-slate-600">{label}</label>
      <input value={addr[key]} onChange={(e) => {
        const value = opts.numeric ? e.target.value.replace(/\D/g, "").slice(0, opts.max || 20) : e.target.value;
        setAddr((a) => ({ ...a, [key]: value }));
      }}
        data-testid={`checkout-${key}`} className="w-full h-11 px-3 mt-1 rounded-lg border border-slate-200 outline-none focus:border-amber-400 text-sm" />
    </div>
  );

  return (
    <div className="container-x py-8">
      <PageHead title="Checkout" crumbs={[{ label: "Home", to: "/" }, { label: "Cart", to: "/cart" }, { label: "Checkout" }]} />
      <div className="grid lg:grid-cols-[1fr_380px] gap-8">
        <div className="space-y-6">
          {!user && (
            <div className="rounded-xl border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-900 mb-3">Contact</h3>
              <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email for order updates" data-testid="checkout-email"
                className="w-full h-11 px-3 rounded-lg border border-slate-200 outline-none focus:border-amber-400 text-sm" />
              <p className="text-xs text-slate-500 mt-2">Have an account? <Link to="/login" className="text-amber-600 font-medium">Log in</Link> for faster checkout.</p>
            </div>
          )}

          <div className="rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-3">Delivery Address</h3>

            {user && savedAddresses.length > 0 && (
              <div className="mb-4" data-testid="saved-address-picker">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-slate-600">Use a saved address</label>
                  <button type="button" onClick={clearAddress} data-testid="enter-new-address-btn" className="text-xs font-semibold text-amber-600 hover:text-amber-700">+ Enter a new address</button>
                </div>
                <div className="grid sm:grid-cols-2 gap-2 mt-2">
                  {savedAddresses.map((a) => (
                    <button key={a.id} type="button" onClick={() => applySavedAddress(a)} data-testid={`use-address-${a.id}`}
                      className="text-left p-3 rounded-lg border border-slate-200 hover:border-amber-400 hover:bg-amber-50/40 transition text-sm">
                      <span className="font-semibold text-slate-900">{a.fullName}
                        {a.isDefault && <span className="text-[10px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded ml-1">Default</span>}
                      </span>
                      <span className="block text-xs text-slate-500 line-clamp-2 mt-0.5">{a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} — {a.pincode}</span>
                      <span className="block text-xs text-slate-400 font-mono mt-0.5">{a.phone}</span>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-2">Selecting a saved address fills the form below — you can still edit it before placing the order.</p>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-3">
              {field("Full Name", "fullName")}
              {field("Phone", "phone", { numeric: true, max: 10 })}
              {field("Address Line 1", "line1", { full: true })}
              {field("Address Line 2 (optional)", "line2", { full: true })}
              {field("City", "city")}
              {field("State", "state")}
              {field("Pincode", "pincode", { numeric: true, max: 6 })}
              {field("Landmark (optional)", "landmark")}
            </div>
            {delivery === "COURIER" && /^\d{6}$/.test(addr.pincode) && (
              <div className="mt-3 text-sm" data-testid="serviceability-status">
                {checkingPin ? (
                  <span className="inline-flex items-center gap-1.5 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Checking delivery availability…</span>
                ) : serviceability?.prepaidEligible === false ? (
                  <span className="inline-flex items-center gap-1.5 text-red-600"><XCircle className="h-4 w-4" /> {serviceability.prepaidEligibleReason || "Prepaid delivery is not available for this pincode. Choose another address or pay cash on delivery if it is offered."}</span>
                ) : serviceability?.serviceable === true ? (
                  <span className="inline-flex items-center gap-1.5 text-emerald-600"><MapPin className="h-4 w-4" /> Delivery available{serviceability.city ? ` to ${serviceability.city}` : ""}.</span>
                ) : serviceability?.serviceable === false ? (
                  <span className="inline-flex items-center gap-1.5 text-red-600"><XCircle className="h-4 w-4" /> {serviceability.message || "Not deliverable to this pincode."}</span>
                ) : serviceability?.error ? (
                  <span className="inline-flex items-center gap-1.5 text-slate-500 text-xs">{serviceability.message || "Could not check delivery availability right now."}</span>
                ) : serviceability?.configured === false ? (
                  <span className="text-slate-400 text-xs">Live delivery check activates once shipping is connected.</span>
                ) : null}
              </div>
            )}
            {delivery === "COURIER" && cityMismatch && (
              <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="city-mismatch-warning">
                This pincode usually maps to <b>{pinCity}{pinState ? `, ${pinState}` : ""}</b>. Please double-check your city and state so your parcel isn't misrouted.
              </div>
            )}
            {delivery === "COURIER" && (
              <label className="flex items-start gap-2 mt-3 text-xs text-slate-600 cursor-pointer">
                <input type="checkbox" checked={addrConfirmed} onChange={(e) => setAddrConfirmed(e.target.checked)} className="accent-amber-500 h-4 w-4 mt-0.5" data-testid="address-confirm-checkbox" />
                <span>I confirm this delivery address is <b>complete and accurate</b>. I understand I'm responsible for the address I provide and that an incorrect address may delay or prevent delivery.</span>
              </label>
            )}
          </div>

          <div className="rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-3">Delivery Method</h3>
            <div className="space-y-2">
              <Option checked={delivery === "COURIER"} onClick={() => setDelivery("COURIER")} title="Courier Delivery" desc="Shipped via Delhivery courier partner" testid="delivery-courier" />
              {store.storePickupEnabled && <Option checked={delivery === "STORE_PICKUP"} onClick={() => setDelivery("STORE_PICKUP")} title="Store Pickup" desc="Collect from our physical store" testid="delivery-pickup" />}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-3">Payment Method</h3>
            <div className="space-y-2">
              <Option checked={payment === "RAZORPAY"} onClick={() => setPayment("RAZORPAY")}
                title="Pay Online (Prepaid)" desc="Secure payment via Razorpay" testid="payment-razorpay" />
              {codAvailable && <Option checked={payment === "COD"} onClick={() => setPayment("COD")}
                title="Cash on Delivery"
                desc={`Pay ${total == null ? "the order total" : formatINR(total)} in cash when your order is delivered`}
                testid="payment-cod" />}
            </div>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Order notes (optional)" rows={2}
              className="w-full mt-3 px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-amber-400" />
          </div>
        </div>

        {/* Summary */}
        <div className="h-fit lg:sticky lg:top-24 rounded-xl border border-slate-200 bg-white p-5" data-testid="checkout-summary">
          <h3 className="font-semibold text-slate-900 mb-4">Order Summary</h3>
          <div className="space-y-2 max-h-52 overflow-y-auto no-scrollbar mb-4">
            {cart.map((it) => (
              <div key={`${it.productId}-${it.variantId}`} className="flex gap-2 items-center text-sm">
                <img src={resolveMediaUrl(it.image)} alt="" onError={handleMediaError} className="h-10 w-10 rounded bg-slate-50 object-contain" />
                <span className="flex-1 line-clamp-1 text-slate-700">{it.name} × {it.quantity}</span>
                <span className="font-mono text-slate-900">{formatINR(it.price * it.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mb-2">
            <div className="relative flex-1">
              <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Coupon code" data-testid="coupon-input"
                className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-amber-400 font-mono" />
            </div>
            <button onClick={applyCoupon} disabled={applying} data-testid="apply-coupon-btn"
              className="px-4 rounded-lg bg-slate-900 text-white text-sm font-semibold disabled:opacity-60">
              {applying ? "Applying…" : "Apply"}
            </button>
          </div>
          {couponError && <p className="text-xs text-red-600 mb-3" data-testid="coupon-error">{couponError}</p>}
          {applied && !couponError && (
            <p className="text-xs text-emerald-600 mb-3" data-testid="coupon-applied">
              Coupon <span className="font-semibold">{applied.code}</span> applied{applied.description ? ` — ${applied.description}` : ""}.{" "}
              <button type="button" onClick={removeCoupon} className="underline text-slate-500 hover:text-red-500">Remove</button>
            </p>
          )}
          <div className="space-y-2 text-sm border-t border-slate-200 pt-4">
            <SumRow label="Subtotal" value={formatINR(subtotal)} />
            {discount > 0 && (
              <SumRow label={`Discount — ${discountLabel}${discountIsAuto ? " (automatic)" : ""}`} value={`− ${formatINR(discount)}`} green />
            )}
            {delivery === "STORE_PICKUP" ? (
              <SumRow label="Shipping" value="Free (store pickup)" />
            ) : shippingPending && !shippingPriced ? (
              <SumRow label="Shipping" value="Calculating…" muted />
            ) : !shippingPriced ? (
              <SumRow label="Shipping" value="Not available" muted />
            ) : (
              <SumRow label="Shipping" value={shipping === 0 ? "Free" : formatINR(shipping)}
                testid="shipping-amount" />
            )}
            {tax > 0 && <SumRow label={`Tax (${quote?.taxPercent || 0}%)`} value={formatINR(tax)} />}
            {handling > 0 && <SumRow label="Handling" value={formatINR(handling)} />}
            {payment === "COD" && codFee > 0 && (
              <SumRow label="COD fee" value={formatINR(codFee)} testid="cod-fee" />
            )}
          </div>
          {/* Honest shipping feedback: a live courier rate that could not be
              determined is explained and never rendered as free shipping. */}
          {delivery === "COURIER" && shippingPriced && shipping > 0 && quote?.shippingMessage && (
            <p className="text-[11px] text-slate-500 mt-2" data-testid="shipping-note">{quote.shippingMessage}</p>
          )}
          {delivery === "COURIER" && !shippingPriced && !quoteLoading && (
            <p className="text-xs text-red-600 mt-2" data-testid="shipping-unavailable">
              {quote?.shippingMessage || "We couldn't calculate the delivery charge for this address. Please retry, or choose store pickup."}
            </p>
          )}
          {delivery === "COURIER" && shippingPriced && shipping === 0 && quote?.shipping?.source === "free_threshold" && (
            <p className="text-[11px] text-emerald-600 mt-2" data-testid="shipping-free-note">{quote.shipping.message}</p>
          )}
          {delivery === "COURIER" && shippingPriced && shippingEnabled && quote?.shipping?.rateAvailable === false && quote?.shipping?.source === "flat_rate_fallback" && (
            <p className="text-[11px] text-amber-600 mt-2" data-testid="shipping-fallback-note">
              Live courier pricing is temporarily unavailable, so the standard delivery charge is shown.
            </p>
          )}
          <div className="flex items-center justify-between border-t border-slate-200 mt-3 pt-3">
            <span className="font-semibold text-slate-900">Total</span>
            <span className="text-xl font-bold font-mono" data-testid="checkout-total">
              {total === null || total === undefined ? "—" : formatINR(total)}
            </span>
          </div>
          {codFee > 0 && (
            <p className="text-[11px] text-slate-500 mt-1" data-testid="cod-fee-note">
              Total includes shipping and the COD fee.
            </p>
          )}
          <label className="flex items-start gap-2 mt-4 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="accent-amber-500 h-4 w-4 mt-0.5" data-testid="terms-checkbox" />
            <span>I agree to the <Link to="/terms" className="text-amber-600">Terms</Link> and <Link to="/return-refund-policy" className="text-amber-600">Refund Policy</Link>.</span>
          </label>
          <button onClick={placeOrder} disabled={placing || notServiceable || (delivery === "COURIER" && !shippingPriced)} data-testid="place-order-btn"
            className="w-full mt-4 inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-slate-900 font-semibold py-3.5 rounded-lg transition">
            {placing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            {payment === "COD" ? "Place COD Order" : "Pay & Place Order"}
          </button>
        </div>
      </div>
    </div>
  );
}

const Option = ({ checked, onClick, title, desc, testid }) => (
  <button onClick={onClick} type="button" data-testid={testid} className={`w-full text-left flex items-start gap-3 p-3 rounded-lg border transition ${checked ? "border-amber-500 bg-amber-50" : "border-slate-200 hover:border-slate-300"}`}>
    <span className={`h-5 w-5 rounded-full border-2 mt-0.5 grid place-items-center shrink-0 ${checked ? "border-amber-500" : "border-slate-300"}`}>
      {checked && <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />}
    </span>
    <span><span className="block font-semibold text-slate-900 text-sm">{title}</span><span className="block text-xs text-slate-500">{desc}</span></span>
  </button>
);

const SumRow = ({ label, value, green, muted, testid }) => (
  <div className="flex items-center justify-between" data-testid={testid}>
    <span className="text-slate-500">{label}</span>
    <span className={`font-mono ${green ? "text-emerald-600" : muted ? "text-slate-400" : "text-slate-900"}`}>{value}</span>
  </div>
);
